import React, { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Copy, Download, Trash2, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function AdminLicenses() {
  const [batches, setBatches] = useState([]);
  const [tests, setTests] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [batchCodes, setBatchCodes] = useState({});
  const [form, setForm] = useState({ test_id: "", count: 10, corporate_name: "", notes: "", expires_at: "" });
  const [newCodes, setNewCodes] = useState(null);

  const load = () => api.get("/admin/license-batches").then(r => setBatches(r.data));
  useEffect(() => {
    load();
    api.get("/admin/tests").then(r => setTests(r.data));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, count: Number(form.count) };
      if (!payload.expires_at) delete payload.expires_at;
      const { data } = await api.post("/admin/licenses/batch", payload);
      setNewCodes(data);
      setForm({ test_id: "", count: 10, corporate_name: "", notes: "", expires_at: "" });
      load();
      toast.success(`${data.count} kode berhasil dibuat`);
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const toggleBatch = async (batchId) => {
    if (expanded === batchId) { setExpanded(null); return; }
    setExpanded(batchId);
    if (!batchCodes[batchId]) {
      const { data } = await api.get(`/admin/licenses?batch_id=${batchId}`);
      setBatchCodes({ ...batchCodes, [batchId]: data });
    }
  };

  const downloadCSV = (batch, codes) => {
    const rows = [["code", "test", "corporate", "status", "used_by", "used_at"]];
    codes.forEach(c => rows.push([c.code, c.test_title, c.corporate_name, c.used_by ? "USED" : "AVAILABLE", c.used_by_email || "", c.used_at || ""]));
    const csv = rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `licenses-${batch.corporate_name || "batch"}-${batch.batch_id.slice(0,8)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const delBatch = async (batchId) => {
    if (!window.confirm("Hapus semua kode yang BELUM TERPAKAI dalam batch ini?")) return;
    const { data } = await api.delete(`/admin/license-batches/${batchId}`);
    toast.success(`${data.deleted} kode dihapus`);
    load();
  };

  return (
    <div data-testid="admin-licenses-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl text-[#1A1728]">Lisensi Tes Korporat</h1>
          <p className="text-[#5C5870] mt-1">Generate kode bulk untuk perusahaan & instansi</p>
        </div>
        <button onClick={() => { setNewCodes(null); setOpen(true); }} className="lumina-btn-primary" data-testid="new-batch-btn"><Plus className="w-4 h-4" /> Batch Baru</button>
      </div>

      <div className="lumina-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F1EBFA]/40 text-[#5C5870] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-6 py-4">Korporat</th>
              <th className="text-left px-6 py-4">Tes</th>
              <th className="text-left px-6 py-4">Penggunaan</th>
              <th className="text-left px-6 py-4">Tanggal</th>
              <th className="text-right px-6 py-4">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {batches.map(b => (
              <React.Fragment key={b.batch_id}>
                <tr className="border-t border-[#E2DFED] hover:bg-[#F1EBFA]/20">
                  <td className="px-6 py-4 font-medium">{b.corporate_name || "—"}<div className="text-xs text-[#5C5870] font-normal">{b.notes}</div></td>
                  <td className="px-6 py-4 text-[#5C5870]">{b.test_title}</td>
                  <td className="px-6 py-4"><span className="text-[#6B4BA3] font-semibold">{b.used}</span> / {b.total}</td>
                  <td className="px-6 py-4 text-[#5C5870]">{new Date(b.created_at).toLocaleDateString("id-ID")}</td>
                  <td className="px-6 py-4 text-right space-x-1">
                    <button onClick={() => toggleBatch(b.batch_id)} className="text-[#6B4BA3] hover:bg-[#E8F0FA] p-2 rounded-lg" data-testid={`expand-${b.batch_id}`}><ChevronDown className={`w-4 h-4 transition-transform ${expanded === b.batch_id ? "rotate-180" : ""}`} /></button>
                    <button onClick={() => downloadCSV(b, batchCodes[b.batch_id] || [])} className="text-[#6B4BA3] hover:bg-[#E8F0FA] p-2 rounded-lg" disabled={!batchCodes[b.batch_id]}><Download className="w-4 h-4" /></button>
                    <button onClick={() => delBatch(b.batch_id)} className="text-[#E57373] hover:bg-red-50 p-2 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
                {expanded === b.batch_id && (
                  <tr><td colSpan={5} className="px-6 py-4 bg-[#FAFCFE]">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {(batchCodes[b.batch_id] || []).map(c => (
                        <div key={c.code} className={`p-2 rounded-lg border text-xs font-mono flex items-center justify-between ${c.used_by ? "bg-gray-100 text-gray-500 border-gray-200" : "bg-white border-[#E2DFED]"}`}>
                          <span>{c.code}</span>
                          {c.used_by ? <span className="text-[10px] uppercase">USED</span> : <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success("Tersalin"); }}><Copy className="w-3 h-3 text-[#6B4BA3]" /></button>}
                        </div>
                      ))}
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {batches.length === 0 && <div className="p-10 text-center text-[#5C5870]">Belum ada batch lisensi.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{newCodes ? "Kode Berhasil Dibuat" : "Batch Lisensi Baru"}</DialogTitle></DialogHeader>
          {newCodes ? (
            <div>
              <div className="bg-[#E8F0FA] rounded-2xl p-4 mb-4">
                <div className="text-sm text-[#5C5870] mb-1">Test: <strong className="text-[#1A1728]">{newCodes.test_title}</strong></div>
                <div className="text-sm text-[#5C5870]">{newCodes.count} kode untuk <strong>{newCodes.corporate_name || "—"}</strong></div>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1 text-xs font-mono mb-4">
                {newCodes.codes.map(c => (
                  <div key={c} className="bg-white border border-[#E2DFED] rounded p-2 flex justify-between">
                    <span>{c}</span>
                    <button onClick={() => { navigator.clipboard.writeText(c); toast.success("Tersalin"); }}><Copy className="w-3 h-3 text-[#6B4BA3]" /></button>
                  </div>
                ))}
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(newCodes.codes.join("\n")); toast.success("Semua kode tersalin"); }}
                className="lumina-btn-secondary w-full"
              ><Copy className="w-4 h-4" /> Copy Semua Kode</button>
              <button onClick={() => setOpen(false)} className="lumina-btn-primary w-full mt-2">Tutup</button>
            </div>
          ) : (
            <form onSubmit={create} className="space-y-4">
              <div>
                <label className="lumina-label block mb-2">Tes</label>
                <Select value={form.test_id} onValueChange={v => setForm({...form, test_id: v})}>
                  <SelectTrigger data-testid="batch-test-select"><SelectValue placeholder="Pilih tes" /></SelectTrigger>
                  <SelectContent>{tests.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="lumina-label block mb-2">Jumlah Kode (1-1000)</label>
                <input type="number" min="1" max="1000" value={form.count} onChange={e=>setForm({...form,count:e.target.value})} className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" data-testid="batch-count" />
              </div>
              <div>
                <label className="lumina-label block mb-2">Nama Perusahaan</label>
                <input value={form.corporate_name} onChange={e=>setForm({...form,corporate_name:e.target.value})} placeholder="PT Lentera Cipta" className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" data-testid="batch-corp" />
              </div>
              <div>
                <label className="lumina-label block mb-2">Catatan</label>
                <input value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Recruitment Q1 2026" className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" />
              </div>
              <div>
                <label className="lumina-label block mb-2">Kadaluarsa (opsional)</label>
                <input type="date" value={form.expires_at?.slice(0,10) || ""} onChange={e=>setForm({...form, expires_at: e.target.value ? e.target.value + "T23:59:59Z" : ""})} className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" />
              </div>
              <button type="submit" disabled={!form.test_id} className="lumina-btn-primary w-full" data-testid="batch-create-btn">Generate {form.count} Kode</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
